import React, { useState, useEffect } from 'react';
import { Box, Grid } from "@mui/material";
import { FaFileImage, FaFilePdf, FaFileLines, FaLink } from "react-icons/fa6";
import { IFrontTask, IEvidence } from "../../../../interfaces/ITask";
import { Button, Input } from 'reactstrap';
import MediaViewer from './MediaViewer';
import { useNotification } from '../../../../hooks/useNotification';
import './Components.css';
import './TaskEvidences.css';
interface TaskEvidencesProps {
  task: IFrontTask;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => void;
  onEvidenceClick: (link: string, id: string) => void;
}

const TaskEvidences: React.FC<TaskEvidencesProps> = ({
  task,
  onFileChange,
  onUpload,
  onEvidenceClick
}) => {
  const { notify } = useNotification();
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null);
  const [showLinkInput, setShowLinkInput] = useState<boolean>(false);
  const [linkUrl, setLinkUrl] = useState<string>('');
  const [isUploadingLink, setIsUploadingLink] = useState<boolean>(false);

  const handleMediaClick = (evidence: IEvidence, index: number) => {
    setSelectedMediaIndex(index);
  };

  const extractDateFromS3Key = (s3Key: string): string => {
    // Tenta extrair data do nome do arquivo ou retorna uma data padrão
    try {
      // Se contém um timestamp ObjectId no começo do caminho, usa ele
      if (s3Key.includes('Tracker/')) {
        const parts = s3Key.split('/');
        if (parts.length >= 2) {
          const taskId = parts[1];
          // Se o taskId parece ser um ObjectId (24 caracteres hex), extrai timestamp
          if (taskId.length === 24 && /^[0-9a-fA-F]+$/.test(taskId)) {
            const timestamp = parseInt(taskId.substring(0, 8), 16);
            return new Date(timestamp * 1000).toLocaleDateString('pt-BR');
          }
        }
      }
      return 'Data não disponível';
    } catch {
      return 'Data não disponível';
    }
  };

  const handleCloseMediaViewer = () => {
    setSelectedMediaIndex(null);
  };

  const handleNavigateMedia = (direction: 'prev' | 'next') => {
    if (selectedMediaIndex !== null && task.evidencies) {
      const newIndex = direction === 'prev' 
        ? (selectedMediaIndex - 1 + task.evidencies.length) % task.evidencies.length
        : (selectedMediaIndex + 1) % task.evidencies.length;
      setSelectedMediaIndex(newIndex);
    }
  };

  const handleAddLink = async () => {
    if (!linkUrl.trim()) {
      notify('Por favor, informe um link válido', { type: 'alert' });
      return;
    }

    // Validação básica de URL
    try {
      new URL(linkUrl);
    } catch {
      notify('URL inválida. Use o formato: https://exemplo.com/arquivo.pdf', { type: 'error' });
      return;
    }

    setIsUploadingLink(true);

    try {
      const response = await fetch(`/Tasks/add-evidence-link/${task.trelloId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ link: linkUrl })
      });

      const result = await response.json();

      if (result.success) {
        notify('✅ Link adicionado com sucesso! Recarregando evidências...', { type: 'success' });
        setLinkUrl('');
        setShowLinkInput(false);
        // Recarregar a página após 1.5 segundos para mostrar a nova evidência
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        notify(result.msg || 'Erro ao adicionar link', { type: 'error' });
      }
    } catch (error) {
      notify('Erro ao adicionar link. Tente novamente.', { type: 'error' });
      console.error('Erro ao adicionar link:', error);
    } finally {
      setIsUploadingLink(false);
    }
  };

  const toggleLinkInput = () => {
    setShowLinkInput(!showLinkInput);
    setLinkUrl('');
  };

  return (
    <div className="evidence-section">
      <div className="evidence-header">
        <h3 className="evidence-title">
          📎 Evidências
        </h3>
      </div>
      
      <div className="evidence-upload-wrapper">
        <Input
          className="evidence-upload-input"
          type="file"
          name="file"
          id="evidencies-files"
          multiple
          onChange={onFileChange}
          placeholder="Selecione arquivos..."
        />
        <Button
          onClick={onUpload}
          className="evidence-upload-button"
          id="evidencies-button"
        >
          📤 Adicionar
        </Button>
        <Button
          onClick={toggleLinkInput}
          className="evidence-link-button"
          color="info"
          outline
        >
          <FaLink /> {showLinkInput ? 'Cancelar' : 'Adicionar Link'}
        </Button>

        {showLinkInput && (
          <>
            <div className="evidence-link-info">
              <small>
              💡 O link será adicionado como uma <strong>Evidência</strong> e aparecerá na lista abaixo
              </small>
            </div>
            <div className="evidence-link-input-group">
              <Input
                type="url"
                placeholder="https://drive.google.com/file/... ou https://exemplo.com/arquivo.pdf"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="evidence-link-input"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddLink();
                  }
                }}
              />
            <Button
              onClick={handleAddLink}
              className="evidence-link-submit"
              color="primary"
              disabled={isUploadingLink || !linkUrl.trim()}
            >
              {isUploadingLink ? 'Adicionando...' : '🔗 Adicionar'}
            </Button>
          </div>
          </>
        )}
      </div>
      <Grid container rowSpacing={1} spacing={1}>
        {task.evidencies != undefined && task.evidencies != null && (
          <>
            {task.evidencies.map((item: IEvidence, index) => {
              // Verifica se é link externo (não hospedado no servidor)
              const isExternalLink = !item.link.includes("files2.aprendaerp.com.br");
              
              // Usa fileName se disponível, caso contrário usa o link como fallback
              const displayName = item.fileName || item.link || 'Link externo';
              const isImage = displayName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)($|\?)/);
              const isPdf = displayName.toLowerCase().includes("pdf");
              
              return (
                <Grid key={index}>
                  <div
                    className="evidence-card"
                    onClick={() => handleMediaClick(item, index)}
                  >
                    {isExternalLink ? (
                      <FaLink size={36} color="#10B981" />
                    ) : isImage ? (
                      <FaFileImage size={36} color="#FAE06E" />
                    ) : isPdf ? (
                      <FaFilePdf size={36} color="#F87171" />
                    ) : (
                      <FaFileLines size={36} color="#60A5FA" />
                    )}
                    <span className="evidence-filename">{displayName.slice(0, 50) || 'Arquivo'}</span>
                    <span className="evidence-date">{extractDateFromS3Key(item.id)}</span>
                  </div>
                </Grid>
              );
            })}
          </>
        )}
      </Grid>

      <MediaViewer
        isOpen={selectedMediaIndex !== null}
        onClose={handleCloseMediaViewer}
        media={selectedMediaIndex !== null && task.evidencies ? task.evidencies[selectedMediaIndex] : null}
        allMedia={task.evidencies || []}
        currentIndex={selectedMediaIndex || 0}
        onNavigate={handleNavigateMedia}
      />
    </div>
  );
};

export default TaskEvidences; 